import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getAllTags, deleteTag, deleteUnusedTags } from '../../services/TagServices';
import styles from './styles/AdminTagsManagement.module.css';

const SORT_OPTIONS = [
    { key: 'count', label: 'Usage Count', defaultOrder: 'desc' },
    { key: 'alpha', label: 'Alphabetical', defaultOrder: 'asc' },
    { key: 'date', label: 'Date Created', defaultOrder: 'desc' },
];

const ORDER_LABELS = {
    count: { desc: '▼', asc: '▲' },
    alpha: { asc: 'A→Z', desc: 'Z→A' },
    date: { desc: 'Newest', asc: 'Oldest' },
};

const AdminTagsManagement = () => {
    const [tags, setTags] = useState([]);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('count');
    const [order, setOrder] = useState('desc');
    const [unusedOnly, setUnusedOnly] = useState(false);
    const [loading, setLoading] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [bulkConfirm, setBulkConfirm] = useState(false);
    const searchTimer = useRef(null);

    const fetchTags = useCallback(async (searchVal, sortByVal, orderVal, unusedVal) => {
        setLoading(true);
        try {
            const params = { sortBy: sortByVal, order: orderVal };
            if (searchVal) params.search = searchVal;
            if (unusedVal) params.unused = 'true';
            const data = await getAllTags(params);
            setTags(data);
        } catch (err) {
            console.error('Failed to fetch tags', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTags(search, sortBy, order, unusedOnly);
    }, [sortBy, order, unusedOnly]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => fetchTags(val, sortBy, order, unusedOnly), 300);
    };

    const handleSortClick = (key) => {
        if (sortBy === key) {
            setOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            const opt = SORT_OPTIONS.find(o => o.key === key);
            setSortBy(key);
            setOrder(opt.defaultOrder);
        }
    };

    const handleToggleUnused = () => {
        setBulkConfirm(false);
        if (!unusedOnly && sortBy === 'count') {
            setSortBy('alpha');
            setOrder('asc');
        }
        setUnusedOnly(prev => !prev);
    };

    const handleDelete = async (tagId) => {
        try {
            await deleteTag(tagId);
            setTags(prev => prev.filter(t => t._id !== tagId));
            setDeleteConfirm(null);
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to delete tag');
        }
    };

    const handleBulkDelete = async () => {
        try {
            await deleteUnusedTags();
            setTags([]);
            setBulkConfirm(false);
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to bulk delete tags');
        }
    };

    const formatDate = (dateStr) =>
        new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.title}>Tags Management</h2>
                <span className={styles.count}>{tags.length} tag{tags.length !== 1 ? 's' : ''}</span>
            </div>

            <div className={styles.controls}>
                <input
                    className={styles.searchInput}
                    type="text"
                    placeholder="Search tags..."
                    value={search}
                    onChange={handleSearchChange}
                />
                <div className={styles.sortGroup}>
                    {SORT_OPTIONS.filter(opt => !(unusedOnly && opt.key === 'count')).map(opt => (
                        <button
                            key={opt.key}
                            className={`${styles.sortBtn} ${sortBy === opt.key ? styles.sortActive : ''}`}
                            onClick={() => handleSortClick(opt.key)}
                        >
                            {opt.label}
                            {sortBy === opt.key && (
                                <span className={styles.orderIndicator}>
                                    {ORDER_LABELS[opt.key][order]}
                                </span>
                            )}
                        </button>
                    ))}
                    <button
                        className={`${styles.sortBtn} ${unusedOnly ? styles.unusedActive : ''}`}
                        onClick={handleToggleUnused}
                    >
                        Unused only
                    </button>
                </div>
            </div>

            {unusedOnly && tags.length > 0 && (
                <div className={styles.bulkBar}>
                    {bulkConfirm ? (
                        <>
                            <span className={styles.bulkWarning}>Delete all {tags.length} unused tag{tags.length !== 1 ? 's' : ''}?</span>
                            <button className={styles.confirmYes} onClick={handleBulkDelete}>Yes, delete all</button>
                            <button className={styles.confirmNo} onClick={() => setBulkConfirm(false)}>Cancel</button>
                        </>
                    ) : (
                        <button className={styles.bulkDeleteBtn} onClick={() => setBulkConfirm(true)}>
                            Delete all unused ({tags.length})
                        </button>
                    )}
                </div>
            )}

            {loading ? (
                <div className={styles.statusMsg}>Loading...</div>
            ) : tags.length === 0 ? (
                <div className={styles.statusMsg}>No tags found.</div>
            ) : (
                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Slug</th>
                                <th>Uses</th>
                                <th>Created</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {tags.map(tag => (
                                <tr key={tag._id} className={styles.row}>
                                    <td className={styles.tagName}>{tag.name}</td>
                                    <td className={styles.tagSlug}>{tag.slug}</td>
                                    <td className={styles.tagCount}>{tag.usageCount}</td>
                                    <td className={styles.tagDate}>{formatDate(tag.createdAt)}</td>
                                    <td className={styles.actionsCell}>
                                        {deleteConfirm === tag._id ? (
                                            <div className={styles.confirmRow}>
                                                <span>Delete?</span>
                                                <button className={styles.confirmYes} onClick={() => handleDelete(tag._id)}>Yes</button>
                                                <button className={styles.confirmNo} onClick={() => setDeleteConfirm(null)}>No</button>
                                            </div>
                                        ) : (
                                            <button className={styles.deleteBtn} onClick={() => setDeleteConfirm(tag._id)}>
                                                Delete
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminTagsManagement;
